import { createEffect, on, createSignal, createResource, createMemo, onCleanup, Show } from "solid-js";
import { useLocation } from "@solidjs/router";
import { ElementNode, activeElement } from "@solidtv/solid";
import { LazyColumn, VirtualRow, LazyRow, useFocusStack } from "@solidtv/solid/primitives";
import { PosterTitle, LandscapeThumbnail, JWAssetPanel, Hero } from "../components";
import styles from "../styles";
import { setGlobalBackground } from "../state";
import { debounce } from "@solid-primitives/scheduled";
import {
  fetchAppConfig,
  fetchPlaylist,
  convertJWItemsToTiles,
  getPosterForWidth,
  JW_APP_CONFIG_ID,
  type JWTile,
  type JWContentItem,
  type JWPlaylistItem
} from "../api/jw";
//import { JWAssetPanel, TileRow } from "../components";

const titleRowStyles = {
  fontFamily: "Raleway",
  fontSize: 25,
  height: 32,
  lineHeight: 32
};

interface RowData {
  configTitle: string;
  playlistId: string;
  featured: boolean;
  items: () => JWTile[] | undefined;
  playlistTitle: () => string;
  type: string;
  height: number;
  marginTop?: number;
}

function HomeCard(props: { item: JWTile; onEnter: () => void }) {
  const [focused, setFocused] = createSignal(false);

  return (
    <view
      width={400}
      height={225}
      onEnter={props.onEnter}
      onFocusChanged={setFocused}
      scale={focused() ? 1.08 : 1}
      transition={{ scale: { duration: 200, easing: "ease-out" } }}
      zIndex={focused() ? 10 : 1}
      item={props.item} // need this for activeElement().item lookup in Home
      forwardStates
    >
      <view
        src={props.item.src}
        onFail={(node) => (node.src = "./assets/fallback.png")}
        width={400}
        height={225}
        borderRadius={8}
      />
      {/* Border overlay */}
      <view
        width={400}
        height={225}
        borderRadius={8}
        border={{ width: focused() ? 4 : 0, color: 0xffffffff }}
        color={0x00000000}
        transition={{ border: { duration: 200, easing: "ease-out" } }}
        zIndex={2}
      />
      {/* Dim overlay */}
      <view
        width={400}
        height={225}
        borderRadius={8}
        color={focused() ? 0x00000000 : 0x00000066}
        transition={{ color: { duration: 200, easing: "ease-out" } }}
        zIndex={3}
      />

      <text y={240} fontSize={24} maxLines={1} contain="width" width={400} color={focused() ? 0xffffffff : 0xccccccff}>
        {props.item.item.title}
      </text>
    </view>
  );
}

function HomeContent(props: { shelves: JWContentItem[] }) {
  const [selectedItem, setSelectedItem] = createSignal<JWPlaylistItem | null>(null);
  const [openPanel, setOpenPanel] = createSignal(false);
  const [maxLoadedRow, setMaxLoadedRow] = createSignal(3);
  const location = useLocation();
  let firstRun = true;

  // Stagger off-screen playlist fetches so initial visible rows' images are not
  // queued behind 18 concurrent playlist JSON requests on old TV browsers.
  const staggerTimer = setInterval(() => {
    setMaxLoadedRow((prev) => {
      if (prev >= props.shelves.length) {
        clearInterval(staggerTimer);
        return prev;
      }
      return prev + 2;
    });
  }, 300);
  onCleanup(() => clearInterval(staggerTimer));

  // Close the panel if the user navigates away using the NavDrawer
  createEffect(() => {
    if (location.pathname !== "/home" && location.pathname !== "/") {
      if (openPanel()) {
        setOpenPanel(false);
        setSelectedItem(null);
      }
    }
  });

  // Create resource for each shelf
  const rows: RowData[] = props.shelves.map((shelf, index) => {
    const [playlistData] = createResource(
      () => (index < maxLoadedRow() ? shelf.contentId : false),
      fetchPlaylist
    );

    const items = createMemo(() => {
      const data = playlistData();
      if (!data?.playlist?.length) return undefined;
      return convertJWItemsToTiles(data.playlist);
    });

    const playlistTitle = () => playlistData()?.title || "";

    return {
      configTitle: shelf.title || "",
      playlistId: shelf.contentId,
      featured: shelf.featured || false,
      items,
      playlistTitle,
      type: shelf.featured ? "Hero" : "PosterTitle",
      height: shelf.featured ? 800 : 330,
      marginTop: 30
    };
  });

  // Active element tracking for background updates
  createEffect(
    on(
      activeElement,
      (elm) => {
        if (!elm) return;
        if (firstRun) {
          setGlobalBackground("./assets/background.webp");
          firstRun = false;
        }
      },
      { defer: true }
    )
  );

  function onRowChanged(
    this: ElementNode,
    selectedIndex: number,
    _column: number,
    _row: ElementNode,
    lastIndex: number
  ) {
    if (selectedIndex === lastIndex) return;
    setMaxLoadedRow((prev) => Math.max(prev, selectedIndex + 4));
  }

  function onCardEnter() {
    const elm = activeElement() as any;
    if (!elm?.item) return;
    const tile = elm.item as JWTile;
    const jwItem = tile.item;
    setSelectedItem(jwItem);
    setOpenPanel(true);
    return true;
  }

  return (
    <view forwardFocus={0}>
      <LazyColumn
        height={1000}
        upCount={3}
        each={rows}
        id="HomeColumn"
        onSelectedChanged={onRowChanged}
        autofocus={rows[0]?.items()}
        gap={10}
        throttleInput={250}
        style={styles.Column}
      >
        {(rowAccessor: () => RowData) => {
          const row = rowAccessor();
          return (
            <view height={row.height} forwardFocus={row.items()?.length ? 1 : 0} marginTop={row.marginTop ?? 30}>
              <text skipFocus style={titleRowStyles}>
                {row.configTitle || row.playlistTitle() || "Loading..."}
              </text>
              <Show
                when={row.items()}
                fallback={<view y={50} width={185} height={278} color={"#333333"} borderRadius={8} />}
              >
                {(items) => (
                  <LazyRow gap={row.type === "Hero" ? 40 : 30} upCount={row.type === "Hero" ? 1 : 5} scroll="center" centerScroll each={items()} y={50}>
                    {(item: () => JWTile, index: number) => {
                      if (row.type === "Hero") {
                        return <Hero onEnter={onCardEnter} item={item()} />;
                      }
                      return <HomeCard onEnter={onCardEnter} item={item()} />;
                    }}
                  </LazyRow>
                )}
              </Show>
            </view>
          );
        }}
      </LazyColumn>
      <JWAssetPanel
        item={selectedItem()}
        open={openPanel()}
        close={() => {
          setOpenPanel(false);
          setSelectedItem(null);
          return true;
        }}
      />
    </view>
  );
}

const Home = () => {
  const [config] = createResource(() => JW_APP_CONFIG_ID, fetchAppConfig);

  const shelves = () => {
    const c = config();
    if (!c?.content) return [];
    return c.content.filter((item: JWContentItem) => item.type === "playlist");
  };

  return (
    <Show
      when={shelves().length}
      fallback={
        <view>
          <text x={162} y={500} fontSize={32}>
            Loading CHEK+ ...
          </text>
        </view>
      }
    >
      <HomeContent shelves={shelves()} />
    </Show>
  );
};

export default Home;
